/**
 * AUTH PROVIDER - TEMPORARY SUPABASE IMPLEMENTATION
 * 
 * IMPORTANT: This auth module uses Supabase Auth temporarily.
 * It is designed to be replaced by OnSiteX Core SSO later.
 * 
 * All authentication logic is isolated in this single module to enable
 * easy swapping without rewriting application pages/components.
 * 
 * When migrating to OnSiteX Core SSO:
 * 1. Replace this file with OnSiteX Core auth provider
 * 2. Maintain the same interface (user, loading, signIn, signOut, etc.)
 * 3. Update requireAuth.jsx to use OnSiteX Core token validation
 * 4. No changes needed in application pages/components
 */

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const { setLanguage } = useLanguage();

  // Tracks the currently-loaded user id so the onChange handler can detect
  // whether a SIGNED_IN / TOKEN_REFRESHED event is for the same user (silent
  // background refresh) or a genuinely new user (needs profile reload).
  const userRef = useRef(null);

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return;
      setProfileLoading(true);
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
        if (!error && data) {
          setUserProfile(data);
          // Sync language preference from profile (persist=false to avoid redundant DB write)
          if (data?.language) {
            setLanguage(data?.language, false);
          }
        }
      } catch (error) {
        console.error('Profile load error:', error);
      } finally {
        setProfileLoading(false);
      }
    },

    clear() {
      setUserProfile(null);
      setProfileLoading(false);
    }
  };

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      const incomingUserId = session?.user?.id ?? null;

      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        // Only reload the profile on initial boot or when a genuinely different
        // user signs in. TOKEN_REFRESHED, and SIGNED_IN / USER_UPDATED for the
        // SAME user are silent background events — the profile is already loaded,
        // so we must NOT call setProfileLoading(true) (which would unmount the
        // app tree via RequireAuth and destroy any open modal + its form data).
        const isSilentRefresh =
          (event === 'TOKEN_REFRESHED') ||
          ((event === 'SIGNED_IN' || event === 'USER_UPDATED') &&
            userRef?.current === incomingUserId);

        if (!isSilentRefresh) {
          profileOperations?.load(session?.user?.id); // Fire-and-forget
        }
        // Always keep the user ref in sync for future comparisons
        userRef.current = incomingUserId;
      } else {
        userRef.current = null;
        profileOperations?.clear();
      }
    }
  };

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session);
    });

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    );

    return () => subscription?.unsubscribe();
  }, []);

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (!error) {
        setUser(null);
        profileOperations?.clear();
      }
      return { error };
    } catch (error) {
      console.error('Sign out error:', error);
      return { error };
    }
  };

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } };
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single();
      
      if (!error && data) {
        setUserProfile(data);
      }
      return { data, error };
    } catch (error) {
      console.error('Update profile error:', error);
      return { data: null, error };
    }
  };

  // Refresh the current user's profile from DB — called after avatar upload
  // so GlobalHeader + sidebar update immediately without a full page reload.
  const refreshProfile = async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();
      if (!error && data) {
        setUserProfile(data);
      }
    } catch (err) {
      console.error('refreshProfile error:', err);
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    refreshProfile,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};